/**
 * YOLO 检测 —— 逐行对拍上游 climb-core/src/detector.rs
 *
 * 口径契约：640 letterbox（最近邻取样、114 填充）、conf 过滤、类无关 NMS IoU<0.45、
 * HOLD_CONF=0.20、检出 <8 时 3×3 半窗滑窗（conf+0.1）后全局 NMS。
 */
import type { InferenceSession } from "onnxruntime-web";
import { ort } from "./ort";

export const YOLO_SIZE = 640;
export const NMS_IOU = 0.45;
export const HOLD_CONF = 0.2;
export const PERSON_CONF = 0.3;

export interface Detection {
  conf: number;
  cls: number;
  /** [x0, y0, x1, y1] 输入图坐标 px */
  box: [number, number, number, number];
}

/** RGBA 图 → 640×640 letterbox CHW float32（最近邻，pad=114/255）。对拍 detector.rs:39-60 */
export function letterbox640(
  rgba: Uint8ClampedArray,
  w: number,
  h: number,
): { input: Float32Array; scale: number; dx: number; dy: number } {
  const scale = Math.min(YOLO_SIZE / w, YOLO_SIZE / h);
  const nw = Math.round(w * scale);
  const nh = Math.round(h * scale);
  const dx = Math.floor((YOLO_SIZE - nw) / 2);
  const dy = Math.floor((YOLO_SIZE - nh) / 2);
  const input = new Float32Array(3 * YOLO_SIZE * YOLO_SIZE);
  for (let y = 0; y < YOLO_SIZE; y++) {
    for (let x = 0; x < YOLO_SIZE; x++) {
      let r = 114,
        g = 114,
        b = 114;
      if (x >= dx && x < dx + nw && y >= dy && y < dy + nh) {
        const sx = Math.min(Math.floor((x - dx) / scale), w - 1);
        const sy = Math.min(Math.floor((y - dy) / scale), h - 1);
        const p = (sy * w + sx) * 4;
        r = rgba[p];
        g = rgba[p + 1];
        b = rgba[p + 2];
      }
      input[y * YOLO_SIZE + x] = r / 255;
      input[YOLO_SIZE * YOLO_SIZE + y * YOLO_SIZE + x] = g / 255;
      input[2 * YOLO_SIZE * YOLO_SIZE + y * YOLO_SIZE + x] = b / 255;
    }
  }
  return { input, scale, dx, dy };
}

/** 输出张量 → Detection[]（未 NMS）。对拍 detector.rs:73-107 */
export function decodeYolo(
  out: Float32Array,
  ch: number,
  anchors: number,
  scale: number,
  dx: number,
  dy: number,
  confMin: number,
  classFilter?: readonly number[],
): Detection[] {
  if (ch < 5 || anchors === 0) return [];
  const nc = ch - 4;
  const dets: Detection[] = [];
  for (let a = 0; a < anchors; a++) {
    let bestC = 0;
    let bestS = -Infinity;
    for (let c = 0; c < nc; c++) {
      const s = out[(4 + c) * anchors + a];
      if (s > bestS) {
        bestS = s;
        bestC = c;
      }
    }
    if (bestS < confMin) continue;
    if (classFilter && !classFilter.includes(bestC)) continue;
    const cx = out[a];
    const cy = out[anchors + a];
    const bw = out[2 * anchors + a];
    const bh = out[3 * anchors + a];
    dets.push({
      conf: bestS,
      cls: bestC,
      box: [
        (cx - bw / 2 - dx) / scale,
        (cy - bh / 2 - dy) / scale,
        (cx + bw / 2 - dx) / scale,
        (cy + bh / 2 - dy) / scale,
      ],
    });
  }
  return dets;
}

export function iou(
  a: readonly [number, number, number, number],
  b: readonly [number, number, number, number],
): number {
  const ix0 = Math.max(a[0], b[0]);
  const iy0 = Math.max(a[1], b[1]);
  const ix1 = Math.min(a[2], b[2]);
  const iy1 = Math.min(a[3], b[3]);
  const inter = Math.max(0, ix1 - ix0) * Math.max(0, iy1 - iy0);
  const ua =
    (a[2] - a[0]) * (a[3] - a[1]) + (b[2] - b[0]) * (b[3] - b[1]) - inter;
  return ua <= 1e-9 ? 0 : inter / ua;
}

/** 类无关 NMS：按 conf 降序，与已保留框 IoU 均 <0.45 才保留。对拍 detector.rs:215-224 */
export function nmsMulti(dets: Detection[]): Detection[] {
  const sorted = [...dets].sort((a, b) => b.conf - a.conf);
  const keep: Detection[] = [];
  for (const d of sorted) {
    if (keep.every((k) => iou(d.box, k.box) < NMS_IOU)) keep.push(d);
  }
  return keep;
}

/** 单图检测（含 letterbox + 推理 + 解码 + NMS） */
export async function detect(
  session: InferenceSession,
  rgba: Uint8ClampedArray,
  w: number,
  h: number,
  confMin: number,
  classFilter?: readonly number[],
): Promise<Detection[]> {
  if (w === 0 || h === 0) return [];
  const { input, scale, dx, dy } = letterbox640(rgba, w, h);
  const tensor = new ort.Tensor("float32", input, [1, 3, YOLO_SIZE, YOLO_SIZE]);
  // #15 第三轮：不硬编码 'images'/'output0'——人物/岩点模型不同来源输入名可能
  // 不同（yolo11n=images，Paddle 系=image），按会话声明的 IO 名动态取名，
  // 名字不匹配时给出可诊断的报错而非 "input 'X' is missing in 'feeds'"。
  const inName =
    (session as { inputNames?: readonly string[] }).inputNames?.[0] ?? "images";
  const results = await session.run({ [inName]: tensor });
  const outKeys = Object.keys(results);
  const outName =
    (session as { outputNames?: readonly string[] }).outputNames?.[0] ??
    (outKeys.length > 0 ? outKeys[0] : "output0");
  const out = results[outName];
  const [, ch, anchors] = out.dims as [number, number, number];
  return nmsMulti(
    decodeYolo(
      out.data as Float32Array,
      ch,
      anchors,
      scale,
      dx,
      dy,
      confMin,
      classFilter,
    ),
  );
}

function cropRgba(
  rgba: Uint8ClampedArray,
  w: number,
  x0: number,
  y0: number,
  cw: number,
  ch: number,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(cw * ch * 4);
  for (let y = 0; y < ch; y++) {
    const src = ((y0 + y) * w + x0) * 4;
    out.set(rgba.subarray(src, src + cw * 4), y * cw * 4);
  }
  return out;
}

/**
 * 岩点检测：整图 + 检出 <8 时 3×3 半窗滑窗（conf+0.1）→ 全局 NMS。
 * 对拍 detector.rs:147-170。
 */
export async function detectHolds(
  session: InferenceSession,
  rgba: Uint8ClampedArray,
  w: number,
  h: number,
): Promise<Detection[]> {
  let out = await detect(session, rgba, w, h, HOLD_CONF);
  if (out.length < 8) {
    for (let gy = 0; gy < 3; gy++) {
      for (let gx = 0; gx < 3; gx++) {
        const cw = Math.floor(w / 2);
        const ch = Math.floor(h / 2);
        if (cw < 64 || ch < 64) continue;
        const x0 = Math.min(Math.floor((w * gx) / 4), w - cw);
        const y0 = Math.min(Math.floor((h * gy) / 4), h - ch);
        const crop = cropRgba(rgba, w, x0, y0, cw, ch);
        const dets = await detect(session, crop, cw, ch, HOLD_CONF + 0.1);
        for (const d of dets) {
          out.push({
            conf: d.conf,
            cls: d.cls,
            box: [
              d.box[0] + x0,
              d.box[1] + y0,
              d.box[2] + x0,
              d.box[3] + y0,
            ],
          });
        }
      }
    }
  }
  return nmsMulti(out);
}

/** 人物检测（可选模型）：COCO person=class 0，conf 0.30 */
export async function detectPersons(
  session: InferenceSession,
  rgba: Uint8ClampedArray,
  w: number,
  h: number,
): Promise<Array<{ conf: number; box: [number, number, number, number] }>> {
  const dets = await detect(session, rgba, w, h, PERSON_CONF, [0]);
  return dets.map(({ conf, box }) => ({ conf, box }));
}
